/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-08
 * Revision: 1.3
 * Description: Main application component with routing and authentication
 */

import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { AuthProvider, useAuth } from '@context/AuthContext'
import { UserContextProvider } from '@context/UserContext'
import { ThemeProvider, useTheme } from '@context/ThemeContext'
import Layout from '@components/Layout'
import Login from '@pages/Login'
import Dashboard from '@pages/Dashboard'
import Systems from '@pages/systems/Systems'
import SystemEditor from '@pages/systems/SystemEditor'
import ViewSystem from '@pages/systems/ViewSystem'
import Products from '@pages/products/Products'
import ProductEditor from '@pages/products/ProductEditor'
import ViewProduct from '@pages/products/ViewProduct'
import Capabilities from '@pages/capabilities/Capabilities'
import CapabilityEditor from '@pages/capabilities/CapabilityEditor'
import ViewCapability from '@pages/capabilities/ViewCapability'
import DataIntegration from '@pages/DataIntegration'
import ContextDigester from '@pages/ContextDigester'
import IntegrationAdmin from '@pages/admin/IntegrationAdmin'
import ChatBot from '@pages/ChatBot'
import BusinessLogic from '@pages/BusinessLogic'
import ContextConfigurator from '@pages/admin/ContextConfigurator'
import KongTopology from '@pages/admin/KongTopology'
import KongHealth from '@pages/admin/KongHealth'
import FinOpsDashboard from '@pages/admin/FinOpsDashboard'
import PartnerTestHealth from '@pages/admin/PartnerTestHealth'
import LmaasMetros from '@pages/LmaasMetros'
import ApiAccessRegister from '@pages/ApiAccessRegister'
import ApiAccessRegistered from '@pages/ApiAccessRegistered'

import ApiAccessWelcome from '@pages/ApiAccessWelcome'
import ApiAccessLogin from '@pages/ApiAccessLogin'
import ApiAccessAuthSetup from '@pages/ApiAccessAuthSetup'
import AdminApiIntegration from '@pages/admin/ApiIntegration'
import InternalDocsAccess from '@pages/InternalDocsAccess'

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="border-primary-600 h-12 w-12 animate-spin rounded-full border-b-2"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public, no Entra auth — gated by admin-issued one-time code */}
      <Route path="/api-access/register" element={<ApiAccessRegister />} />
      <Route path="/api-access/registered" element={<ApiAccessRegistered />} />
      <Route path="/api-access/verify" element={<Navigate to="/api-access/login" replace />} />
      <Route path="/api-access/welcome" element={<ApiAccessWelcome />} />
      <Route path="/api-access/auth-setup" element={<ApiAccessAuthSetup />} />
      <Route path="/api-access/login" element={<ApiAccessLogin />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/systems"
        element={
          <ProtectedRoute>
            <Layout>
              <Systems />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/systems/new"
        element={
          <ProtectedRoute>
            <Layout>
              <SystemEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/systems/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ViewSystem />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/systems/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <SystemEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/systems/business-logic"
        element={
          <ProtectedRoute>
            <Layout>
              <BusinessLogic />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Layout>
              <Products />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/create"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ViewProduct />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capabilities"
        element={
          <ProtectedRoute>
            <Layout>
              <Capabilities />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capabilities/create"
        element={
          <ProtectedRoute>
            <Layout>
              <CapabilityEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capabilities/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ViewCapability />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capabilities/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <CapabilityEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/integration/data"
        element={
          <ProtectedRoute>
            <Layout>
              <DataIntegration />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/integration/context-digester"
        element={
          <ProtectedRoute>
            <Layout>
              <ContextDigester />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integration"
        element={
          <ProtectedRoute>
            <Layout>
              <IntegrationAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chatbot"
        element={
          <ProtectedRoute>
            <Layout>
              <ChatBot />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/integration" element={<Navigate to="/integration/data" replace />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Layout>
              <ContextConfigurator />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kong-topology"
        element={
          <ProtectedRoute>
            <Layout>
              <KongTopology />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kong-health"
        element={
          <ProtectedRoute>
            <Layout>
              <KongHealth />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/finops"
        element={
          <ProtectedRoute>
            <Layout>
              <FinOpsDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partner-test-health"
        element={
          <ProtectedRoute>
            <Layout>
              <PartnerTestHealth />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/indirect-partners/lmaas-metros"
        element={
          <ProtectedRoute>
            <Layout>
              <LmaasMetros />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/api-integration"
        element={
          <ProtectedRoute>
            <Layout>
              <AdminApiIntegration />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Back-compat: old admin URL redirects to the new API Integration view */}
      <Route path="/admin/api-access-requests" element={<Navigate to="/admin/api-integration" replace />} />
      <Route
        path="/internal-docs"
        element={
          <ProtectedRoute>
            <InternalDocsAccess />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

const ThemedToastContainer: React.FC = () => {
  const { resolvedTheme } = useTheme()
  return (
    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={resolvedTheme}
    />
  )
}

const App: React.FC = () => {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <UserContextProvider>
            <AppRoutes />
            <ThemedToastContainer />
          </UserContextProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  )
}

export default App
